<template>
  <v-container fluid class="container">
    <Breadcrumbs :items="breadcrumbs" />

    <v-toolbar flat>
      <ejs-dropdownlist
        id="accrualTypeDropdownlist"
        ref="accrualTypeDropdown"
        :dataSource="accrualTypeDropdownDataSource"
        :change="onAccrualTypeDropdownChange"
        v-model="accrualTypeDropdown"
        placeholder="Accrual Type"
        width="150"
      />
      <v-spacer />
      <v-btn color="primary" @click="excelDownloadClick">
        <v-icon left>mdi-file-excel</v-icon>
        Export to Excel
      </v-btn>
    </v-toolbar>

    <v-spacer />

    <ejs-grid
      ref="grid"
      v-if="items.length && periods.length"
      :key="`grid-${reportDropdownValue}`"
      :dataSource="items"
      :filterSettings="{type: 'Menu'}"
      :searchSettings='searchOptions'
      :allowFiltering='true'
      :allowPaging='true'
      :pageSettings='pageSettings'
      :height="560"
      :allowExcelExport="true"
      :allowTextWrap="true"
      :excelHeaderQueryCellInfo="excelHeader"
    >
      <e-columns>
        <!-- Static columns -->
        <e-column field="type" headerText="Leasing Type" width="80" />
        <e-column field="product" headerText="Product" width="100" />
        <e-column field="contract_id" headerText="Contract ID" width="200" />
        <e-column field="client_segment" headerText="Segment" width="100" />
        <e-column field="customer" headerText="Customer" width="100" />
        <e-column field="currency" headerText="Currency" width="80" />
        <e-column field="accrual_type" headerText="Accrual Type" width="100" />
        <e-column field="balance" headerText="Balance" width="120" format="n2" textAlign="Right" />
        <e-column field="support" headerText="Support" width="100" format="n2" textAlign="Right" />
        <e-column field="principal" headerText="Principal" width="120" format="n2" textAlign="Right" />

        <!-- Dynamic column groups - only balance for accrual data -->
        <e-column textAlign="center" headerText="Outstanding Balance" :allowSorting="false" :columns="balanceColumns" />
      </e-columns>
    </ejs-grid>

    <Snackbar :text="notificationText" :type="notificationType" />

    <v-overlay :value="loader" :z-index="1000">
      <v-progress-circular indeterminate size="64" />
    </v-overlay>

  </v-container>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapState } from 'vuex';
import {
  GridPlugin,
  ExcelExport,
  Filter,
  Page,
} from '@syncfusion/ej2-vue-grids';
import Breadcrumbs from '@/components/Breadcrumbs';
import Snackbar from '@/components/Snackbar';
import api from '@/api/lbt-api';

Vue.use(GridPlugin);

export default {
  name: 'ExistingLoansPositions',
  components: { Breadcrumbs, Snackbar },
  data() {
    return {
      notificationText: '',
      notificationType: null,
      loader: false,
      periods: [],
      items: [],
      accrualTypeDropdown: 'All Types',
      accrualTypeDropdownValue: 'all',
      accrualTypeDropdownDataSource: [
        'All Types',
        'Rent',
        'Fee',
        'Subvention',
        'Deposit',
      ],
      searchOptions: {
        fields: ['contract_id', 'type', 'product', 'currency', 'client_segment', 'customer'],
        operator: 'contains',
        key: '',
        ignoreCase: true,
      },
      pageSettings: {
        pageSize: 50,
        pageSizes: [25, 50, 100, 200, 'All'],
        enableQueryString: false,
      },
      breadcrumbs: [
        { translationKey: 'breadcrumbs_plannings', route: '/plannings', disabled: false },
        { translationKey: 'existing_loans_positions', disabled: true },
      ],
      // Dynamic column arrays
      balanceColumns: [],
    };
  },
  computed: {
    ...mapGetters('translations', ['translate']),
    ...mapState('plannings', ['activePlanning']),
  },
  created() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loader = true;
      try {
        const payload = await api.getExistingLoansForPlanning(
          this.$route.params.planningId,
          'accrual',
          'eur',
          this.accrualTypeDropdownValue,
        );

        this.periods = payload.periods || [];

        // Data is already in the correct flat structure from the API
        const transformedItems = (payload.rows || []).map(item => ({
          ...item, // All the flat properties (balance_0, interest_0, etc.) are already included
          // Add any additional computed fields if needed
          client_segment: item.client_segment,
          product: item.product,
          customer: item.customer,
        }));

        this.items = transformedItems;

        // Build dynamic columns after data is ready
        this.buildAllColumns();

        // Force grid refresh
        this.$nextTick(() => {
          if (this.$refs.grid && this.$refs.grid.ej2Instances) {
            const grid = this.$refs.grid.ej2Instances[0];
            if (grid && grid.refresh) {
              grid.refresh();
            }
          }
        });
      } catch (e) {
        this.notificationText = e.message || 'Failed to load existing loans data';
        this.notificationType = 'error';
        setTimeout(() => { this.notificationText = null; }, 3000);
      } finally {
        this.loader = false;
      }
    },

    // Build outstanding balance columns dynamically
    buildBalanceColumns() {
      this.balanceColumns = [];
      this.periods.forEach((period, index) => {
        const colDef = {
          field: `balance_${index}`,
          headerText: index.toString(),
          headerValueAccessor: this.lookupPeriodHeader,
          width: 100,
          format: 'n2',
          textAlign: 'Right',
          allowSorting: false,
          allowFiltering: false,
        };
        if (index === 0) {
          colDef.customAttributes = { class: 'group-1-first' };
        } else {
          colDef.customAttributes = { class: 'group-1' };
        }
        this.balanceColumns.push(colDef);
      });
    },

    // Build interest payment columns dynamically
    buildInterestColumns() {
      this.interestColumns = [];
      this.periods.forEach((period, index) => {
        this.interestColumns.push({
          field: `interest_${index}`,
          headerText: index.toString(),
          headerValueAccessor: this.lookupPeriodHeader,
          width: 100,
          format: 'n2',
          textAlign: 'Right',
          allowSorting: false,
          allowFiltering: false,
        });
      });
    },

    // Build principal payment columns dynamically
    buildPrincipalColumns() {
      this.principalColumns = [];
      this.periods.forEach((period, index) => {
        this.principalColumns.push({
          field: `principal_${index}`,
          headerText: index.toString(),
          headerValueAccessor: this.lookupPeriodHeader,
          width: 100,
          format: 'n2',
          textAlign: 'Right',
          allowSorting: false,
          allowFiltering: false,
        });
      });
    },

    // Header value accessor
    lookupPeriodHeader(idx) {
      return this.periods[Number(idx)] || '';
    },

    onAccrualTypeDropdownChange({ value }) {
      // Map display text to API values
      const mappings = {
        'All Types': 'all',
        Rent: 'rent',
        Fee: 'fee',
        Subvention: 'subv',
        Deposit: 'depo',
      };
      this.accrualTypeDropdownValue = mappings[value] || 'all';
      this.fetchData();
    },

    async excelDownloadClick() {
      const grid = document.getElementsByClassName('e-grid')[0].ej2_instances[0];
      if (!grid) {
        return;
      }

      const suffixByHeader = {
        'Outstanding Balance': '_BAL',
      };

      // Flatten stacked headers in place so Excel gets a single header row
      // (no "Outstanding Balance" parent row) and period columns are suffixed.
      const originalColumns = grid.columns;
      const flatColumns = [];
      const headerTextSnapshots = [];

      originalColumns.forEach((col) => {
        if (col.columns && col.columns.length) {
          const suffix = suffixByHeader[col.headerText] || '';
          col.columns.forEach((child) => {
            const m = String(child.field || '').match(/_(\d+)$/);
            const idx = m ? parseInt(m[1], 10) : 0;
            const periodLabel = this.periods[idx] || child.headerText || '';
            headerTextSnapshots.push({ column: child, headerText: child.headerText });
            // eslint-disable-next-line no-param-reassign
            child.headerText = `${periodLabel}${suffix}`;
            flatColumns.push(child);
          });
        } else {
          flatColumns.push(col);
        }
      });

      grid.columns = flatColumns;
      try {
        await grid.excelExport({
          exportType: 'AllPages',
          fileName: `Existing Loans Positions - ${this.accrualTypeDropdown} - Planning - ${this.activePlanning.year}-${this.activePlanning.month}.xlsx`,
        });
      } finally {
        headerTextSnapshots.forEach((snap) => {
          // eslint-disable-next-line no-param-reassign
          snap.column.headerText = snap.headerText;
        });
        grid.columns = originalColumns;
        if (typeof grid.refreshColumns === 'function') {
          grid.refreshColumns();
        }
      }
    },

    excelHeader(args) {
      const { cell } = args;
      const col = args.gridCell && args.gridCell.column;
      if (col && col.field && col.field.startsWith('balance_')) {
        if (typeof cell.value === 'string' && /^\d+$/.test(cell.value)) {
          cell.value = this.lookupPeriodHeader(cell.value);
        }
      }
    },


    // Build all column types
    buildAllColumns() {
      this.buildBalanceColumns();
    },

  },
  provide: { grid: [ExcelExport, Filter, Page] },
};
</script>

<style>
@import '~@syncfusion/ej2-vue-treegrid/styles/material.css';

.e-grid .e-headercell.highlight-cell,
.highlight-cell {
  background: #eeeeee;
}

.e-valid-input {
  margin-right: 10px;
}

.e-headercelldiv {
  text-align: center !important;
}

/* Column group dividers - add thicker borders between groups */
.e-grid .e-stackedheadercell {
  border-left: 3px solid #1976d2 !important;
  background: linear-gradient(to bottom, #e3f2fd 0%, #bbdefb 100%);
  font-weight: 600;
}

/* First column group doesn't need left border */
.e-grid .e-stackedheadercell:first-of-type {
  border-left: none !important;
}

/* Alternating background colors for column groups */
.e-grid .e-stackedheadercell:nth-child(odd) {
  background: linear-gradient(to bottom, #fff3e0 0%, #ffe0b2 100%);
}

.e-grid .e-stackedheadercell:nth-child(even) {
  background: linear-gradient(to bottom, #e8f5e9 0%, #c8e6c9 100%);
}

/* Apply dividers to first column of each group */
.e-grid .e-rowcell.group-1-first {
  border-left: 3px solid #1976d2 !important;
}

/* Apply background colors to all columns in each group */
/* Group 1 - Outstanding Balance */
.e-grid .e-row .e-rowcell.group-1,
.e-grid .e-row .e-rowcell.group-1-first {
  background-color: #fff9f5;
}
.e-grid .e-altrow .e-rowcell.group-1,
.e-grid .e-altrow .e-rowcell.group-1-first {
  background-color: #fff5eb;
}
</style>
